import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';
import { CMYKMark } from '../components/landing/CMYKMark';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });

    if (err) {
      const msg = (err.message || '').toLowerCase();
      if (msg.includes('rate') || msg.includes('too many')) {
        setError('Muitas tentativas em sequência. Aguarde alguns instantes antes de tentar novamente.');
      } else {
        // Não revela se o e-mail existe — sempre mostramos sucesso.
        setSent(true);
      }
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-paper bt-paper-grain">
      <div className="px-6 md:px-10 pt-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-small font-semibold text-ink-500 hover:text-ink-900 transition-colors btn-focus rounded-md py-1"
        >
          <ArrowLeft size={16} />
          Voltar para login
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 md:px-10 py-8">
        <div className="w-full max-w-md">
          <div className="mb-7">
            <Logo variant="on-light" size="lg" markSize={32} />
          </div>

          <p className="bt-eyebrow text-green-700 mb-2">Recuperar acesso</p>
          <h1 className="font-display font-bold text-h1 text-ink-900 leading-tight tracking-[-0.02em]">
            Esqueceu a senha?
          </h1>
          <p className="font-body text-body text-ink-700 mt-2">
            Sem stress. Informe seu e-mail e enviaremos um link para criar uma nova senha.
          </p>

          {sent ? (
            <div className="mt-6 bg-green-100 border border-green-700 text-green-900 text-small rounded-lg p-4 inline-flex items-start gap-2">
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              <span>
                Se este e-mail estiver cadastrado, você receberá um link para redefinir a senha em
                alguns minutos. Verifique sua caixa de entrada e a pasta de spam.
              </span>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-ruby-100 border border-ruby-500 text-ruby-700 text-small rounded-lg p-3 mt-5 mb-2 inline-flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <div>
                  <label htmlFor="email" className="block bt-eyebrow text-ink-500 mb-1.5">
                    E-mail
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
                    />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(ev) => setEmail(ev.target.value)}
                      required
                      placeholder="seu@email.com"
                      autoComplete="email"
                      className="w-full h-11 pl-9 pr-3 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  iconRight={ArrowRight}
                  loading={loading}
                >
                  Enviar link de redefinição
                </Button>
              </form>
            </>
          )}

          <p className="text-center text-caption text-ink-500 mt-5">
            Lembrou a senha?{' '}
            <Link
              to="/login"
              className="font-semibold text-green-700 underline btn-focus rounded-sm"
            >
              Voltar para o login.
            </Link>
          </p>
        </div>
      </div>

      <div className="px-6 md:px-10 pb-6 flex items-center justify-between text-caption text-ink-500">
        <span className="bt-eyebrow text-ink-500/70">BoraTrocar</span>
        <CMYKMark className="opacity-30" />
      </div>
    </div>
  );
}
